import { usePolling } from "./usePolling";
import { useCallback, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { listBookingRows } from "../utils/directBookingsApi";
import { checkoutTimingForRoom } from "../utils/checkoutTiming";

// Every open admin tab polls; usePolling skips hidden tabs and never overlaps
// requests. 60 s keeps alerts prompt without loading the server.
const POLL_INTERVAL_MS = 60_000;
const ACK_KEY = "checkout-due-acknowledged";

export interface CheckoutDueAlert {
  roomBookingId: number;
  roomNo: string | null;
  roomTypeName: string | null;
  guestName: string | null;
}

const readAcked = (): Set<number> | null => {
  try {
    const raw = localStorage.getItem(ACK_KEY);
    return raw === null ? null : new Set<number>(JSON.parse(raw));
  } catch {
    return null;
  }
};

const writeAcked = (ids: Set<number>) => {
  try {
    localStorage.setItem(ACK_KEY, JSON.stringify(Array.from(ids).slice(-500)));
  } catch {
    // Storage blocked: the alert just comes back on the next reload.
  }
};

/** Watches for checked-in rooms that have reached their check-out time (see roomDueAt) and keeps
 * an alert up until the desk acknowledges it. Mounted once, in AdminLayout. The first run on a
 * browser treats the rooms already overdue as seen, so only rooms that fall due afterwards ring. */
export const useCheckoutDueAlerts = () => {
  const { user } = useAuth();
  const accountId = Number(user?.accountId) || 0;
  const [alerts, setAlerts] = useState<CheckoutDueAlert[]>([]);

  const poll = useCallback(async () => {
    if (!accountId) return;
    try {
      // Only the overdue rooms, worked out by the server — not every booking
      // the hotel has ever had, which is what this used to download each minute.
      const page = await listBookingRows({ page: 1, pageSize: 200, overdueOnly: true });
      const now = Date.now();
      const overdue: CheckoutDueAlert[] = [];
      page.items.forEach(({ roomBookingId, directBooking: b }) => {
        const r = (b.rooms ?? []).find((room) => room.roomBookingId === roomBookingId);
        if (r && r.statusCode === "CHECKED_IN" && checkoutTimingForRoom(r, now)?.state === "overdue") {
          overdue.push({
            roomBookingId: r.roomBookingId,
            roomNo: r.roomNo,
            roomTypeName: r.roomTypeName,
            guestName: b.guestName,
          });
        }
      });
      let acked = readAcked();
      if (acked === null) {
        acked = new Set(overdue.map((r) => r.roomBookingId));
        writeAcked(acked);
      }
      const fresh = overdue.filter((r) => !acked!.has(r.roomBookingId));
      setAlerts((prev) =>
        prev.length === fresh.length && prev.every((p, i) => p.roomBookingId === fresh[i].roomBookingId)
          ? prev
          : fresh
      );
    } catch {
      // Best-effort — the next tick tries again.
    }
  }, [accountId]);

  usePolling(poll, POLL_INTERVAL_MS);

  const acknowledgeAll = useCallback(() => {
    const acked = readAcked() ?? new Set<number>();
    alerts.forEach((r) => acked.add(r.roomBookingId));
    writeAcked(acked);
    setAlerts([]);
  }, [alerts]);

  return { alerts, acknowledgeAll };
};
